import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Schema } from '@/services/api/schemas'

export interface DeleteSchemaDialogProps {
  target: Schema | null
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  /** Case mode: the case currently runs this schema. Deleting it resets the case
   * to the global default (the binding is cleared server-side). */
  inUse?: boolean
}

/** Confirmation dialog for permanently deleting a schema version. */
export function DeleteSchemaDialog({
  target,
  onOpenChange,
  onConfirm,
  inUse = false,
}: DeleteSchemaDialogProps) {
  return (
    <Dialog open={!!target} onOpenChange={o => !o && onOpenChange(false)}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Delete schema version?</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            This will permanently delete{' '}
            <span className="text-foreground font-medium">
              {target?.name} v{target?.version}
            </span>{' '}
            and all its entities, relations, and patterns. This cannot be undone.
            {target?.is_active && (
              <span className="block mt-2 text-warning">Warning: this is the active schema.</span>
            )}
            {inUse && (
              <span className="block mt-2 text-warning">
                Warning: this case is currently using this schema. Deleting it resets the case to
                the global default schema.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={onConfirm} className="bg-destructive hover:bg-destructive/90 text-white">
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
